/**
 * Centralised image loading.
 *
 * - Service worker retries transient GCS failures (see service-worker.js)
 * - `preloadImage` / `preloadImages` handle JS-side preloading with concurrency
 * - `useRetryableSrc` gives <img> tags DOM-level retry when the SW isn't enough
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { Telemetry } from '@/lib/telemetry';

const RETRY_DELAY_MS = 500;
const MAX_IMG_RETRIES = 3;

/**
 * Hook for <img> tags that retries on load failure.
 *
 * Returns props to spread onto <img>. On error it waits briefly then
 * re-triggers the load by appending a cache-buster, giving the service
 * worker a fresh chance.
 *
 * Usage: `<img {...useRetryableSrc(url)} alt="..." />`
 */
export function useRetryableSrc(src: string | undefined) {
  const attempt = useRef(0);
  const prevSrc = useRef(src);
  const [activeSrc, setActiveSrc] = useState(src);

  // Reset when the source URL itself changes
  if (src !== prevSrc.current) {
    prevSrc.current = src;
    attempt.current = 0;
    setActiveSrc(src);
  }

  const onError = useCallback(() => {
    if (!src) return;
    if (attempt.current >= MAX_IMG_RETRIES) {
      Telemetry.captureImageLoadError(src, { retryCount: MAX_IMG_RETRIES, source: 'retryable-src' });
      return;
    }
    attempt.current++;
    // Append a retry param so the browser treats it as a new request.
    // The service worker strips query params for caching, so this is safe.
    setTimeout(() => {
      const sep = src.includes('?') ? '&' : '?';
      setActiveSrc(`${src}${sep}_retry=${attempt.current}`);
    }, RETRY_DELAY_MS);
  }, [src]);

  return { src: activeSrc, onError };
}

/** Preload a single image. Resolves to the URL on success. */
export function preloadImage(url: string): Promise<string> {
  return new Promise<string>((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(url);
    image.onerror = () => reject(new Error(`Failed to load image: ${url}`));
    image.src = url;
  });
}

/**
 * React hook that manages a `preloadImages` queue with automatic
 * setup/teardown. Rebuilds the queue when `entries` changes (callers
 * should memoize). Returns a `reprioritize` function for live reordering
 * without tearing down the queue.
 */
export function useImagePreloader<T extends { url: string; priority?: number }>(
  entries: T[],
  options: {
    onLoad?: (entry: T) => void;
    onError?: (entry: T, error: unknown) => void;
  },
): { reprioritize: (getPriority: (entry: T) => number) => void } {
  const handleRef = useRef<PreloadHandle<T> | null>(null);
  // Use a ref for callbacks so the queue isn't rebuilt when they change
  const optionsRef = useRef(options);
  optionsRef.current = options;

  useEffect(() => {
    handleRef.current?.cancel();
    handleRef.current = preloadImages(entries, {
      onLoad: (entry) => optionsRef.current.onLoad?.(entry),
      onError: (entry, error) => optionsRef.current.onError?.(entry, error),
    });
    return () => { handleRef.current?.cancel(); handleRef.current = null; };
  }, [entries]);

  const reprioritize = useCallback((getPriority: (entry: T) => number) => {
    handleRef.current?.reprioritize(getPriority);
  }, []);

  return { reprioritize };
}

export interface PreloadHandle<T> {
  cancel: () => void;
  /** Re-sort the pending queue by new priorities. In-flight loads finish naturally. */
  reprioritize: (getPriority: (entry: T) => number) => void;
}

/**
 * Preload multiple images with concurrency control and prioritisation.
 *
 * If entries have a `priority` field (lower = first), the queue always
 * picks the highest-priority remaining entry. Otherwise entries load
 * in the order given.
 *
 * Uses a slow-start strategy: begins with 6 concurrent loads to
 * establish the HTTP/2 connection, then ramps up after the first
 * success since subsequent requests multiplex on the warm connection.
 *
 * Returns a handle with `cancel` and `reprioritize` methods.
 */
export function preloadImages<T extends { url: string; priority?: number }>(
  entries: T[],
  options: {
    concurrency?: number;
    onLoad?: (entry: T) => void;
    onError?: (entry: T, error: unknown) => void;
  } = {},
): PreloadHandle<T> {
  const { concurrency = 24, onLoad, onError } = options;
  const queue = [...entries];
  // Sort by priority if present (lower = higher priority)
  if (queue.length > 0 && queue[0].priority != null) {
    queue.sort((a, b) => (a.priority ?? 0) - (b.priority ?? 0));
  }
  let cancelled = false;
  let active = 0;
  let connected = false;

  function limit() {
    // Start with 6 to establish the TLS connection, then ramp up
    // once HTTP/2 multiplexing is available on the warm connection
    return connected ? concurrency : 6;
  }

  function next() {
    if (cancelled) return;
    while (active < limit() && queue.length > 0) {
      const entry = queue.shift()!;
      active++;
      preloadImage(entry.url).then(
        () => {
          active--;
          if (!cancelled) {
            if (!connected) connected = true;
            onLoad?.(entry);
          }
          next();
        },
        (error) => {
          active--;
          if (!cancelled) onError?.(entry, error);
          next();
        },
      );
    }
  }

  next();
  return {
    cancel: () => { cancelled = true; },
    reprioritize: (getPriority) => {
      queue.sort((a, b) => getPriority(a) - getPriority(b));
    },
  };
}
